import Phaser from 'phaser';
import { PlayerPosition } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { PlayerPositionConfig } from '../../../config/positions';

export interface DisconnectNotificationCallbacks {
  onReplaceWithAI: (playerId: string) => void;
  toDisplayPosition: (serverPosition: PlayerPosition) => PlayerPosition;
  getPositionConfig: () => Record<PlayerPosition, PlayerPositionConfig>;
  isHost: () => boolean;
}

/**
 * Manages disconnect notifications for players who have left the game.
 * Shows "Disconnected" overlay with optional "Replace with AI" button for the host.
 */
export class DisconnectNotifications {
  private scene: Phaser.Scene;
  private notifications: Map<string, Phaser.GameObjects.Container> = new Map();
  private callbacks: DisconnectNotificationCallbacks;

  constructor(scene: Phaser.Scene, callbacks: DisconnectNotificationCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
  }

  /**
   * Show a disconnect notification for a player.
   */
  show(playerId: string, playerName: string, position: PlayerPosition): void {
    if (this.notifications.has(playerId)) return;

    const displayPos = this.callbacks.toDisplayPosition(position);
    const posConfig = this.callbacks.getPositionConfig()[displayPos];

    const container = this.scene.add.container(posConfig.label.x, posConfig.label.y + 40);
    container.setDepth(120);

    // Background
    const bg = this.scene.add
      .rectangle(0, 0, 120, 50, PERSIAN_THEME.colors.darkOverlay, 0.9)
      .setStrokeStyle(1, 0xff6666);

    // Player name
    const label = this.scene.add
      .text(0, -12, playerName, {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '11px',
        color: '#ff6666',
      })
      .setOrigin(0.5);

    // Status text
    const statusText = this.scene.add
      .text(0, 2, 'Disconnected', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '10px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5);

    container.add([bg, label, statusText]);

    // "Replace with AI" button (host only)
    if (this.callbacks.isHost()) {
      const btnBg = this.scene.add
        .rectangle(0, 20, 90, 18, PERSIAN_THEME.colors.bronze, 0.9)
        .setInteractive({ useHandCursor: true });

      const btnText = this.scene.add
        .text(0, 20, 'Replace AI', {
          fontFamily: PERSIAN_THEME.fonts.body,
          fontSize: '10px',
          color: '#ffffff',
        })
        .setOrigin(0.5);

      btnBg.on('pointerover', () => btnBg.setFillStyle(PERSIAN_THEME.colors.gold));
      btnBg.on('pointerout', () => btnBg.setFillStyle(PERSIAN_THEME.colors.bronze));
      btnBg.on('pointerdown', () => this.callbacks.onReplaceWithAI(playerId));

      container.add([btnBg, btnText]);
      bg.setSize(120, 60);
    }

    this.notifications.set(playerId, container);

    // Fade in
    container.setAlpha(0);
    this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: 300,
      ease: 'Cubic.easeOut',
    });
  }

  /**
   * Hide and destroy a disconnect notification.
   */
  hide(playerId: string): void {
    const container = this.notifications.get(playerId);
    if (!container) return;

    this.scene.tweens.add({
      targets: container,
      alpha: 0,
      duration: 200,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        container.destroy();
        this.notifications.delete(playerId);
      },
    });
  }

  /**
   * Hide all disconnect notifications.
   */
  hideAll(): void {
    this.notifications.forEach((container, playerId) => {
      container.destroy();
      this.notifications.delete(playerId);
    });
  }

  destroy(): void {
    this.hideAll();
  }
}
